import { countByAsRecord, nonNullable } from 'lib/array/arrayUtils'
import loadScript from 'lib/window/loadScript'

export const ARENA_SCRIPT_SRC = 'https://static.ticketswitch.com/arena/v1.0.2/main.js'

type ArenaPositionValue = 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'none'
type ArenaBorderValue = 'none' | 'all' | 'top' | 'bottom' | 'left' | 'right' | 'top-bottom' | 'left-right'

interface ArenaPosition {
  position: ArenaPositionValue
  border?: ArenaBorderValue
}

export type ArenaStatus = 'idle' | 'creating-session' | 'loading-script' | 'creating-arena' | 'ready' | 'reserving' | 'reserved' | 'failed'
export type ArenaErrorType =
  | 'arena-api-unavailable'
  | 'arena-create-failed'
  | 'event-failed'
  | 'missing-trolley-token'
  | 'reserve-failed'
  | 'script-load-failed'
  | 'selection-session-failed'

export interface ArenaConfigOptions {
  controls?: ArenaPosition
  disableScrollToZoom?: boolean
  hasCustomLegend?: boolean
  hideBanner?: boolean
  hideCurrencySymbol?: boolean
  hideOfferMessages?: boolean
  legend?: ArenaPosition
  mapPaddingBottom?: number
  mapPaddingRight?: number
  minimap?: ArenaPosition
  showBasket?: boolean
  unhandledSeats?: ArenaPosition
  zoomDepthOnSeatClick?: number
}

export interface ArenaCreateOptions {
  apiAuthToken: string
  apiBase: string
  eventId: string
  perfId: string
  tyrBase: string
  options: ArenaConfigOptions
}

export interface ArenaReserveResult {
  trolleyToken?: string
  trolley_token?: string
}

export interface ArenaApp {
  on: (eventName: ArenaEventName, listener: (payload: ArenaEventPayload) => void) => void
  off?: (eventName: ArenaEventName, listener: (payload: ArenaEventPayload) => void) => void
  destroy?: () => void
  reserveSeats?: () => Promise<ArenaReserveResult | string | undefined>
}

export interface ArenaCurrency {
  code: string
  factor: number
}

interface ArenaSeatBlock {
  desc?: string
  price_band_code?: string
  ticket_type_code?: string
}

export interface ArenaSelectedSeat {
  avgRating?: number
  bgColor?: string
  borderColor?: string
  discountCode?: string
  discountId?: string
  finance?: {
    discountCode?: string
    priceBandCode?: string
    ticketTypeCode?: string
  }
  isRestrictedView?: boolean
  key?: string
  label?: string
  price?: number
  priceBandCode?: string
  restrictedViewText?: string
  seatBlock?: ArenaSeatBlock | null
  seatId: string
  ticketTypeCode?: string
  ticketTypeDesc?: string
  uuid?: string
}

interface ArenaSeatSelectionPayload {
  currency?: ArenaCurrency
  selectedSeats?: Array<ArenaSelectedSeat>
}

interface ArenaTrolleyPayload {
  trolleyToken?: string
  trolley_token?: string
}

export interface ArenaSendMethod {
  send_code: string
  source_code?: string
  sourceCode?: string
}

interface ArenaSendMethodsPayload {
  sendMethods?: Array<ArenaSendMethod>
}

export type ArenaEventName =
  | 'init'
  | 'event-load-success'
  | 'event-load-fail'
  | 'seatmap-load-success'
  | 'seatmap-load-fail'
  | 'performance-load-success'
  | 'performance-load-fail'
  | 'availability-load-success'
  | 'availability-load-fail'
  | 'send-methods-load-success'
  | 'send-methods-load-fail'
  | 'trolley-load-success'
  | 'trolley-load-fail'
  | 'seat-selection'

export type ArenaEventPayload = ArenaSeatSelectionPayload | ArenaTrolleyPayload | ArenaSendMethodsPayload | Record<string, unknown>

export interface ArenaSelectionResult {
  currency?: ArenaCurrency
  selectedSeats: Array<ArenaSelectedSeat>
  trolleyToken: string
  totalPrice: number
}

export interface ArenaTrolleyItem {
  costPrice?: number
  currency?: string
  priceBandCode: string
  salePrice?: number
  ticketTypeCode: string
}

export interface ArenaProviderBookingData extends Record<string, unknown> {
  arenaTrolleyToken: string
  selectedSeats: Array<ArenaSelectedSeat>
  trolleyItems: Array<ArenaTrolleyItem>
}

export interface ArenaEventStateUpdate {
  currency?: ArenaCurrency
  error?: ArenaErrorType
  failedEventName?: ArenaEventName
  sendMethod?: ArenaSendMethod
  selectedSeats?: Array<ArenaSelectedSeat>
  status?: ArenaStatus
  trolleyToken?: string
  shouldClearTrolleyToken?: boolean
}

export const defaultArenaOptions: ArenaConfigOptions = {
  controls: { position: 'bottom-right', border: 'all' },
  disableScrollToZoom: false,
  hasCustomLegend: false,
  hideBanner: true,
  hideCurrencySymbol: false,
  hideOfferMessages: false,
  legend: { position: 'bottom', border: 'top' },
  mapPaddingBottom: 24,
  mapPaddingRight: 24,
  minimap: { position: 'none' },
  showBasket: false,
  unhandledSeats: { position: 'none' },
  zoomDepthOnSeatClick: 1,
}

export const arenaEvents: Array<ArenaEventName> = [
  'init',
  'event-load-success',
  'event-load-fail',
  'seatmap-load-success',
  'seatmap-load-fail',
  'performance-load-success',
  'performance-load-fail',
  'availability-load-success',
  'availability-load-fail',
  'send-methods-load-success',
  'send-methods-load-fail',
  'trolley-load-success',
  'trolley-load-fail',
  'seat-selection',
]

const arenaScriptPromises = new Map<string, Promise<void>>()

function hasSeatSelectionPayload(payload: ArenaEventPayload): payload is ArenaSeatSelectionPayload {
  return 'selectedSeats' in payload
}

function hasTrolleyPayload(payload: ArenaEventPayload): payload is ArenaTrolleyPayload {
  return 'trolleyToken' in payload || 'trolley_token' in payload
}

function hasSendMethodsPayload(payload: ArenaEventPayload): payload is ArenaSendMethodsPayload {
  return 'sendMethods' in payload
}

export function loadArenaScript(scriptSrc: string) {
  const existingPromise = arenaScriptPromises.get(scriptSrc)
  if (existingPromise) return existingPromise

  const nextPromise = loadScript(scriptSrc, { async: true })
    .then(() => {
      arenaScriptPromises.delete(scriptSrc)
    })
    .catch((error) => {
      arenaScriptPromises.delete(scriptSrc)
      throw error
    })
  arenaScriptPromises.set(scriptSrc, nextPromise)
  return nextPromise
}

export function clearArenaScriptCache(scriptSrc?: string) {
  if (scriptSrc) {
    arenaScriptPromises.delete(scriptSrc)
    return
  }
  arenaScriptPromises.clear()
}

export function getTrolleyToken(payload: ArenaTrolleyPayload | ArenaReserveResult | string | undefined) {
  if (!payload) return undefined
  if (typeof payload === 'string') return payload
  return payload.trolleyToken ?? payload.trolley_token
}

export function getSeatPrice(seat: ArenaSelectedSeat, currency?: ArenaCurrency) {
  if (seat.price === undefined) return undefined
  const factor = currency?.factor ?? 1
  return factor > 1 ? seat.price / factor : seat.price
}

export function getTotalPrice(seats: Array<ArenaSelectedSeat>, currency?: ArenaCurrency) {
  return seats.reduce((total, seat) => total + (getSeatPrice(seat, currency) ?? 0), 0)
}

export function getArenaSeatTicketTypeCode(seat: ArenaSelectedSeat) {
  return seat.ticketTypeCode ?? seat.finance?.ticketTypeCode ?? seat.seatBlock?.ticket_type_code
}

export function getArenaSeatPriceBandCode(seat: ArenaSelectedSeat) {
  return seat.priceBandCode ?? seat.finance?.priceBandCode ?? seat.seatBlock?.price_band_code
}

export function getArenaSeatDiscountCode(seat: ArenaSelectedSeat) {
  return seat.discountCode ?? seat.discountId ?? seat.finance?.discountCode ?? 'NORMAL'
}

export function getArenaSendMethodSourceCode(sendMethod: ArenaSendMethod) {
  return sendMethod.source_code ?? sendMethod.sourceCode ?? 'email'
}

export interface ArenaReservableSeatGroup {
  discountCode: string
  priceBandCode: string
  seats: Array<ArenaSelectedSeat>
  ticketTypeCode: string
}

export function getArenaReservableSeatGroups(seats: Array<ArenaSelectedSeat>): Array<ArenaReservableSeatGroup> {
  const groups = new Map<string, ArenaReservableSeatGroup>()

  seats.forEach((seat) => {
    const ticketTypeCode = getArenaSeatTicketTypeCode(seat)
    const priceBandCode = getArenaSeatPriceBandCode(seat)
    if (!ticketTypeCode || !priceBandCode) return

    const discountCode = getArenaSeatDiscountCode(seat)
    const key = `${ticketTypeCode}:${priceBandCode}:${discountCode}`
    const group = groups.get(key) ?? {
      discountCode,
      priceBandCode,
      seats: [],
      ticketTypeCode,
    }

    group.seats.push(seat)
    groups.set(key, group)
  })

  return Array.from(groups.values())
}

export function hasMixedArenaReservableSeatGroups(seats: Array<ArenaSelectedSeat>) {
  return getArenaReservableSeatGroups(seats).length > 1
}

function getArenaProductIdCandidates(performanceId: string | undefined, ticketTypeCode: string, priceBandCode: string) {
  if (!performanceId) return []

  const priceBandCodes = new Set([
    priceBandCode,
    priceBandCode.includes('/') ? priceBandCode.split('/')[0] : `${priceBandCode}/pool`,
  ])

  return Array.from(priceBandCodes).map(code => `${performanceId}:${ticketTypeCode}:${code}`)
}

export function getArenaTicketCounts(
  selection: ArenaSelectionResult,
  selectedTimeSlot: App.ExperienceAvailabilityTimeSlot,
): Record<string, number> {
  const ticketsByProductId = new Map(selectedTimeSlot.tickets.map(ticket => [ticket.productId, ticket]))

  const ticketIds = selection.selectedSeats.map((seat) => {
    const ticketTypeCode = getArenaSeatTicketTypeCode(seat)
    const priceBandCode = getArenaSeatPriceBandCode(seat)
    if (!ticketTypeCode || !priceBandCode) return undefined

    const matchingProductId = getArenaProductIdCandidates(
      selectedTimeSlot.providerData?.performanceId,
      ticketTypeCode,
      priceBandCode,
    ).find(productId => ticketsByProductId.has(productId))

    return matchingProductId ? ticketsByProductId.get(matchingProductId)?.id : undefined
  })

  return countByAsRecord(nonNullable(ticketIds), ticketId => ticketId)
}

export function getArenaTrolleyItems(selection: ArenaSelectionResult): Array<ArenaTrolleyItem> {
  const trolleyItems = new Map<string, ArenaTrolleyItem>()

  selection.selectedSeats.forEach((seat) => {
    const ticketTypeCode = getArenaSeatTicketTypeCode(seat)
    const priceBandCode = getArenaSeatPriceBandCode(seat)
    if (!ticketTypeCode || !priceBandCode) return

    const key = `${ticketTypeCode}:${priceBandCode}`
    if (trolleyItems.has(key)) return

    trolleyItems.set(key, {
      ticketTypeCode,
      priceBandCode,
      salePrice: seat.price,
      costPrice: seat.price,
      currency: selection.currency?.code,
    })
  })

  return Array.from(trolleyItems.values())
}

export function getArenaProviderBookingData(selection: ArenaSelectionResult): ArenaProviderBookingData {
  return {
    arenaTrolleyToken: selection.trolleyToken,
    selectedSeats: selection.selectedSeats,
    trolleyItems: getArenaTrolleyItems(selection),
  }
}

export function getArenaEventStateUpdate(eventName: ArenaEventName, payload: ArenaEventPayload): ArenaEventStateUpdate {
  if (eventName.endsWith('-fail')) {
    return {
      error: 'event-failed',
      failedEventName: eventName,
      status: 'failed',
    }
  }

  if (eventName === 'seat-selection' && hasSeatSelectionPayload(payload)) {
    return {
      currency: payload.currency,
      error: undefined,
      selectedSeats: [...(payload.selectedSeats ?? [])],
      shouldClearTrolleyToken: true,
      status: 'ready',
    }
  }

  if (eventName === 'trolley-load-success' && hasTrolleyPayload(payload)) {
    return {
      error: undefined,
      status: 'reserved',
      trolleyToken: getTrolleyToken(payload),
    }
  }

  if (eventName === 'send-methods-load-success' && hasSendMethodsPayload(payload)) {
    return {
      error: undefined,
      sendMethod: payload.sendMethods?.[0],
    }
  }

  return { error: undefined }
}
